// NPM packages
import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import styled from '@emotion/styled';
import { DateTime } from 'luxon';

// All other imports
import PageContent from 'components/core/PageContent';
import H1 from 'components/core/H1';

const Root = styled(PageContent)({});

const StyledTable = styled(Table)(({ theme: { palette, typography } }) => ({
  '& tr:nth-of-type(even)': {
    backgroundColor: palette.grey[100],
  },
  '& thead tr': {
    backgroundColor: palette.grey[300],
  },
  '& thead th': {
    fontWeight: typography.fontWeightBold,
    fontSize: '1rem',
  },
}));
const DateCell = styled(TableCell)({
  whiteSpace: 'nowrap',
});
const TypeCell = styled(TableCell)({
  width: '15rem',
});

enum EventType {
  STAKEHOLDER_MEETING = 'Stakeholder Group Meeting',
  OPEN_SCIENCE_WEBINAR = 'Open Science and FAIR Data Working Group Webinar',
  OUTREACH_MEETING = 'Outreach Presentation',
  LIVE_DEMONSTRATION = 'Live Demonstration',
  TRAINING_WORKSHOP = 'Training Workshop',
}

const events = [
  {
    type: EventType.OUTREACH_MEETING,
    date: DateTime.local(2024, 7, 2),
    description:
      'Presentation on MSD-LIVE as part of a PNNL briefing to DOE BER program managers',
  },
  {
    type: EventType.OUTREACH_MEETING,
    date: DateTime.local(2024, 6, 18),
    description:
      'Presentation on MSD-LIVE to the Integrated Hydro-Terrestrial Modeling (IHTM) interagency working group',
  },
  {
    type: EventType.TRAINING_WORKSHOP,
    date: DateTime.local(2024, 5, 6),
    description: 'Hands-on model training sessions at the GCAM Annual Meeting',
  },
  {
    type: EventType.OUTREACH_MEETING,
    date: DateTime.local(2024, 3, 18),
    description:
      'Presentation on MSD-LIVE as part of a PNNL briefing to DOE BER program managers',
  },
  {
    type: EventType.TRAINING_WORKSHOP,
    date: DateTime.local(2024, 2, 29),
    description:
      'Presentation on MSD-LIVE and hands-on UC eBook training session at the IM3 Annual Meeting',
  },
  {
    type: EventType.TRAINING_WORKSHOP,
    date: DateTime.local(2024, 2, 22),
    description:
      'MSD Community of Practice webinar on using MSD-LIVE and meta-repositories to support open MSD science',
  },
  {
    type: EventType.STAKEHOLDER_MEETING,
    date: DateTime.local(2024, 2, 9),
    description: 'Meeting of the MSD-LIVE Stakeholder Group',
  },
  {
    type: EventType.OUTREACH_MEETING,
    date: DateTime.local(2023, 10, 24),
    description: 'Presentation on MSD-LIVE at the DOE Data Days Workshop',
  },
  {
    type: EventType.TRAINING_WORKSHOP,
    date: DateTime.local(2023, 10, 4),
    description:
      'Hands-on data repository training session at the MSD Community of Practice Meeting',
  },
  {
    type: EventType.OUTREACH_MEETING,
    date: DateTime.local(2023, 7, 18),
    description: "Presentation on MSD-LIVE as part of PNNL's TechFest",
  },
  {
    type: 'v2 Release of MSD-LIVE',
    date: DateTime.local(2023, 6, 28),
    description: 'Version 2 of MSD-LIVE launches to the MSD community',
  },
  {
    type: EventType.OUTREACH_MEETING,
    date: DateTime.local(2023, 6, 27),
    description:
      'MSD-LIVE v2 release preview at the Snowmass Energy Modeling Forum',
  },
];

export interface EventsProps {
  className?: string;
}

export default function Events(props: EventsProps): React.ReactElement | null {
  const { className } = props;
  return (
    <Root className={className}>
      <H1>Events</H1>
      <TableContainer component={Paper}>
        <StyledTable>
          <TableHead>
            <TableRow>
              <TableCell>Type</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Description</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {events.map((event, index) => (
              <TableRow
                // index is OK since events never change
                key={index}
              >
                <TypeCell>{event.type}</TypeCell>
                <DateCell>
                  {event.date.toLocaleString(DateTime.DATE_MED)}
                </DateCell>
                <TableCell>{event.description}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </StyledTable>
      </TableContainer>
    </Root>
  );
}
